import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Note from "../../library/Note";
import PromoCodeFormDialog from "./actions/PromoCodeFormDialog";
import PromoCodeDetailDialog from "./actions/PromoCodeDetailDialog";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  CODE_PATTERN,
  PARTNER_PATTERN,
  STATUSES,
  apiMessage,
  bodyCellSx,
  formatDateTime,
  headCellSx,
  normalizeResponseData,
  paperBorderSx,
  parseDiscount,
  parseExpiryDays,
  statusColor,
  statusLabel,
  toDateTimeInputValue,
} from "./partnerShared";
import {
  createPromoCode,
  deletePromoCode,
  getPromoCodeDetail,
  listPromoCodes,
  updatePromoCode,
} from "../../../contexts/services/admin/promoCodeServices";

const emptyCodeForm = (partnerName) => ({
  mode: "create",
  id: null,
  original: null,
  promo_code: "",
  partner_name: partnerName || "",
  discount_value: "",
  expiry_days: "",
  expires_at: "",
  external_reference_id: "",
  status: "ISSUED",
  redeemed_by_user_id: "",
  clearUser: false,
  applyToSubscription: true,
});

const codeFormFromRecord = (record) => ({
  mode: "edit",
  id: record.id,
  original: record,
  promo_code: record.promo_code || "",
  partner_name: record.partner_name || "",
  discount_value:
    record.discount_value === null || record.discount_value === undefined
      ? ""
      : String(record.discount_value),
  expiry_days: "",
  expires_at: toDateTimeInputValue(record.expires_at),
  external_reference_id: record.external_reference_id || "",
  status: record.status || "ISSUED",
  redeemed_by_user_id: record.redeemed_by_user_id || "",
  clearUser: false,
  applyToSubscription: true,
});

function buildCodeUpdate(form) {
  const original = form.original || {};
  const body = {};
  const lines = [];
  const promo = form.promo_code.trim().toUpperCase();
  if (!promo || !CODE_PATTERN.test(promo)) {
    return { error: "Code must be 4 to 32 letters, numbers, or hyphens." };
  }
  if (promo !== original.promo_code) {
    body.promo_code = promo;
    lines.push(`Code becomes ${promo}.`);
  }

  const partner = form.partner_name.trim().toLowerCase();
  if (!PARTNER_PATTERN.test(partner)) return { error: "Choose a partner." };
  if (partner !== original.partner_name) {
    body.partner_name = partner;
    lines.push(`Partner becomes ${partner}.`);
  }

  const discount = parseDiscount(form.discount_value);
  if (discount === null)
    return { error: "Discount must be a whole number from 0 to 100." };
  if (discount !== Number(original.discount_value)) {
    body.discount_value = discount;
    body.apply_to_subscription = form.applyToSubscription;
    lines.push(
      form.applyToSubscription
        ? `Discount becomes ${discount}%, and the linked subscription is updated.`
        : `Discount becomes ${discount}%. The subscription discount stays as it is.`
    );
  }

  if (!form.expires_at) return { error: "Expiry date is required." };
  const expiresAt = new Date(form.expires_at);
  if (Number.isNaN(expiresAt.getTime()))
    return { error: "Expiry date is invalid." };
  const sameMinute =
    Math.floor(expiresAt.getTime() / 60000) ===
    Math.floor(new Date(original.expires_at).getTime() / 60000);
  if (!sameMinute) {
    body.expires_at = expiresAt.toISOString();
    lines.push(`Expiry becomes ${formatDateTime(expiresAt)}.`);
  }

  if (!STATUSES.includes(form.status)) return { error: "Status is invalid." };
  if (form.status !== original.status) {
    body.status = form.status;
    lines.push(`Status becomes ${form.status}.`);
  }

  const nextRef = form.external_reference_id.trim();
  if (nextRef.length > 128)
    return { error: "Reference must be 128 characters or fewer." };
  if (nextRef !== (original.external_reference_id || "")) {
    body.external_reference_id = nextRef || null;
    lines.push(
      nextRef
        ? `Reference becomes ${nextRef}.`
        : "The reference will be cleared."
    );
  }

  if (form.clearUser && original.redeemed_by_user_id) {
    body.redeemed_by_user_id = null;
    lines.push("The linked user will be removed.");
  } else if (
    !form.clearUser &&
    form.redeemed_by_user_id.trim() &&
    form.redeemed_by_user_id.trim() !== (original.redeemed_by_user_id || "")
  ) {
    body.redeemed_by_user_id = form.redeemed_by_user_id.trim();
    lines.push("The code will be linked to the user id you entered.");
  }

  if (!lines.length)
    return { error: "Change at least one field before saving." };
  return { body, lines };
}

export default function PromoCodesTab({
  active = true,
  campaigns,
  campaignsLoading,
  campaignsError,
  busy,
  onReview,
  onSnack,
}) {
  const [draftQuery, setDraftQuery] = useState({
    q: "",
    status: "",
    redeemed: "",
    partner_name: "",
  });
  const [appliedQuery, setAppliedQuery] = useState({
    q: "",
    status: "",
    redeemed: "",
    partner_name: "",
    page: 1,
    limit: 20,
  });
  const [list, setList] = useState({
    promo_codes: [],
    total: 0,
    page: 1,
    limit: 20,
  });
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [codeForm, setCodeForm] = useState(null);
  const [codeFormError, setCodeFormError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const appliedQueryRef = useRef(appliedQuery);
  appliedQueryRef.current = appliedQuery;

  const loadCodes = useCallback(async (next) => {
    setListLoading(true);
    setListError("");
    try {
      const params = { page: next.page, limit: next.limit };
      if (next.partner_name) params.partner_name = next.partner_name;
      if (next.q) params.q = next.q;
      if (next.status) params.status = next.status;
      if (next.redeemed) params.redeemed = next.redeemed;
      const response = await listPromoCodes(params);
      const data = normalizeResponseData(response);
      setList({
        promo_codes: Array.isArray(data.promo_codes) ? data.promo_codes : [],
        total: Number(data.total) || 0,
        page: Number(data.page) || next.page,
        limit: Number(data.limit) || next.limit,
      });
      setAppliedQuery(next);
    } catch (error) {
      setListError(apiMessage(error, "Could not load promo codes."));
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    // Partner edits can update discounts and expiry on existing codes.
    if (active) void loadCodes(appliedQueryRef.current);
  }, [active, loadCodes]);

  const search = () =>
    loadCodes({
      ...appliedQuery,
      ...draftQuery,
      page: 1,
    });

  const openDetail = async (id) => {
    setDetail({ id });
    setDetailError("");
    setDetailLoading(true);
    try {
      const response = await getPromoCodeDetail(id);
      setDetail(normalizeResponseData(response));
    } catch (error) {
      setDetailError(apiMessage(error, "Could not load this promo code."));
    } finally {
      setDetailLoading(false);
    }
  };

  const askToSaveCode = () => {
    if (!codeForm || busy) return;
    setCodeFormError("");
    if (codeForm.mode === "create") {
      const partner = codeForm.partner_name.trim().toLowerCase();
      if (!campaigns.some((campaign) => campaign.partner_name === partner)) {
        setCodeFormError("Choose a partner from the list.");
        return;
      }
      const promo = codeForm.promo_code.trim().toUpperCase();
      if (promo && !CODE_PATTERN.test(promo)) {
        setCodeFormError("Code must be 4 to 32 letters, numbers, or hyphens.");
        return;
      }
      const nextDiscount =
        codeForm.discount_value === ""
          ? null
          : parseDiscount(codeForm.discount_value);
      if (codeForm.discount_value !== "" && nextDiscount === null) {
        setCodeFormError("Discount must be a whole number from 0 to 100.");
        return;
      }
      const nextExpiry =
        codeForm.expiry_days === ""
          ? null
          : parseExpiryDays(codeForm.expiry_days);
      if (codeForm.expiry_days !== "" && nextExpiry === null) {
        setCodeFormError(
          "Expiry must be a whole number of days from 1 to 3650."
        );
        return;
      }
      if (codeForm.external_reference_id.trim().length > 128) {
        setCodeFormError("Reference must be 128 characters or fewer.");
        return;
      }
      const campaign = campaigns.find((item) => item.partner_name === partner);
      const snapshot = {
        partner_name: partner,
        ...(promo ? { promo_code: promo } : {}),
        ...(nextDiscount !== null ? { discount_value: nextDiscount } : {}),
        ...(nextExpiry !== null ? { expiry_days: nextExpiry } : {}),
        ...(codeForm.external_reference_id.trim()
          ? { external_reference_id: codeForm.external_reference_id.trim() }
          : {}),
      };
      onReview({
        title: "Create promo code",
        confirmLabel: "Create code",
        summary: (
          <Typography variant='body2'>
            {promo ? `Create ${promo}` : "We'll make a code"} for {partner}.{" "}
            {nextDiscount === null
              ? `It uses that partner's usual offer (${campaign?.discount_value}% off, lasting ${campaign?.expiry_days} days) unless you set something here.`
              : `This code is ${nextDiscount}% off.`}
          </Typography>
        ),
        run: (reason) => saveCode("create", null, { ...snapshot, reason }),
      });
      return;
    }

    const update = buildCodeUpdate(codeForm);
    if (update.error) {
      setCodeFormError(update.error);
      return;
    }
    onReview({
      title: "Update promo code",
      confirmLabel: "Save changes",
      summary: (
        <Stack spacing={0.75}>
          {update.lines.map((line) => (
            <Typography key={line} variant='body2'>
              {line}
            </Typography>
          ))}
        </Stack>
      ),
      run: (reason) =>
        saveCode("edit", codeForm.id, { ...update.body, reason }),
    });
  };

  const saveCode = async (mode, id, body) => {
    try {
      const response =
        mode === "create"
          ? await createPromoCode(body)
          : await updatePromoCode(id, body);
      const result = normalizeResponseData(response);
      setCodeForm(null);
      onSnack({
        message:
          mode === "create"
            ? `Promo code ${result.promo_code || ""} created.`
            : `Promo code ${result.promo_code || ""} updated.`,
      });
      await loadCodes(appliedQuery);
    } catch (error) {
      setCodeFormError(apiMessage(error, "Could not save this promo code."));
    }
  };

  const askToDelete = (row) => {
    onReview({
      title: "Delete promo code",
      confirmLabel: "Delete code",
      summary: (
        <Typography variant='body2'>
          Delete {row.promo_code}. Anyone who already got this discount keeps
          it.
        </Typography>
      ),
      run: async (reason) => {
        try {
          await deletePromoCode(row.id, { reason });
          onSnack({ message: `${row.promo_code} deleted.` });
          const nextPage =
            list.promo_codes.length === 1 && appliedQuery.page > 1
              ? appliedQuery.page - 1
              : appliedQuery.page;
          await loadCodes({ ...appliedQuery, page: nextPage });
        } catch (error) {
          onSnack({
            message: apiMessage(error, "Could not delete this promo code."),
            severity: "error",
          });
        }
      },
    });
  };

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Paper
        component='section'
        aria-labelledby='promo-codes-title'
        elevation={0}
        sx={{
          ...paperBorderSx,
          boxShadow: "0 3px 16px rgba(30,20,50,0.025)",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent='space-between'
              gap={2}
            >
              <Stack direction='row' alignItems='center' gap={1}>
                <Typography
                  id='promo-codes-title'
                  component='h2'
                  variant='subtitle1'
                  fontWeight={700}
                >
                  Promo codes
                </Typography>
                {!listLoading && !listError && (
                  <Chip
                    size='small'
                    label={`${list.total} ${list.total === 1 ? "code" : "codes"}`}
                    sx={{ height: 24, fontWeight: 600 }}
                  />
                )}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant='outlined'
                  startIcon={
                    listLoading ? (
                      <CircularProgress size={16} color='inherit' />
                    ) : (
                      <RefreshRoundedIcon />
                    )
                  }
                  disabled={busy || listLoading}
                  onClick={() => loadCodes(appliedQuery)}
                >
                  Refresh
                </Button>
                <Button
                  variant='contained'
                  startIcon={<AddRoundedIcon />}
                  disabled={
                    busy ||
                    campaignsLoading ||
                    Boolean(campaignsError) ||
                    campaigns.length === 0
                  }
                  onClick={() => {
                    setCodeFormError("");
                    setCodeForm(
                      emptyCodeForm(
                        appliedQuery.partner_name || campaigns[0]?.partner_name
                      )
                    );
                  }}
                  sx={{ boxShadow: "none", whiteSpace: "nowrap" }}
                >
                  Create code
                </Button>
              </Stack>
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Codes from partners, and any you create yourself. Open a row to
              see who used it.
            </Typography>
            {list.promo_codes.length > 0 && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: { xs: "block", md: "none" }, mt: 1 }}
              >
                Scroll the table sideways to see expiry dates and actions.
              </Typography>
            )}
          </Box>

          {campaignsError ? (
            <Note severity='warning' sx={{ m: 2 }}>
              {campaignsError} Open Manage partners to retry loading partners
              before creating a code.
            </Note>
          ) : !campaignsLoading && campaigns.length === 0 ? (
            <Note severity='info' sx={{ m: 2 }}>
              Create a partner using Manage partners above before creating your
              first promo code.
            </Note>
          ) : null}

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <TextField
              select
              label='Partner'
              size='small'
              value={draftQuery.partner_name}
              disabled={busy}
              onChange={(event) =>
                setDraftQuery((current) => ({
                  ...current,
                  partner_name: event.target.value,
                }))
              }
              sx={{ minWidth: { md: 180 } }}
            >
              <MenuItem value=''>All partners</MenuItem>
              {campaigns.map((campaign) => (
                <MenuItem
                  key={campaign.partner_name}
                  value={campaign.partner_name}
                >
                  {campaign.partner_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label='Search code or reference'
              value={draftQuery.q}
              size='small'
              fullWidth
              disabled={busy}
              onChange={(event) =>
                setDraftQuery((current) => ({
                  ...current,
                  q: event.target.value,
                }))
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") void search();
              }}
            />
            <TextField
              select
              label='Status'
              size='small'
              value={draftQuery.status}
              disabled={busy}
              onChange={(event) =>
                setDraftQuery((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
              sx={{ minWidth: { md: 150 } }}
            >
              <MenuItem value=''>Any status</MenuItem>
              {STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label='Redeemed'
              size='small'
              value={draftQuery.redeemed}
              disabled={busy}
              onChange={(event) =>
                setDraftQuery((current) => ({
                  ...current,
                  redeemed: event.target.value,
                }))
              }
              sx={{ minWidth: { md: 160 } }}
            >
              <MenuItem value=''>Anyone</MenuItem>
              <MenuItem value='true'>Linked to a user</MenuItem>
              <MenuItem value='false'>Not linked</MenuItem>
            </TextField>
            <Button
              variant='outlined'
              disabled={busy || listLoading}
              onClick={search}
            >
              Search
            </Button>
          </Stack>

          {listError && (
            <Note
              severity='error'
              variant='outlined'
              sx={{ m: 2 }}
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={() => loadCodes(appliedQuery)}
                  disabled={busy || listLoading}
                >
                  Retry
                </Button>
              }
            >
              {listError}
            </Note>
          )}
        </Box>

        {listLoading ? (
          <Stack
            role='status'
            direction='row'
            spacing={1.5}
            justifyContent='center'
            alignItems='center'
            sx={{ p: 5, flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <CircularProgress size={20} />
            <Typography variant='body2' color='text.secondary'>
              Loading promo codes...
            </Typography>
          </Stack>
        ) : list.promo_codes.length === 0 && !listError ? (
          <Stack
            alignItems='center'
            spacing={1}
            sx={{
              px: 2,
              py: 5,
              textAlign: "center",
              flex: 1,
              minHeight: 0,
              justifyContent: "center",
              overflow: "auto",
            }}
          >
            <ConfirmationNumberOutlinedIcon
              sx={{ color: "text.disabled", fontSize: 32 }}
            />
            <Typography variant='subtitle2' fontWeight={700}>
              No promo codes
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Nothing here yet. Create a code, or wait for a partner to request
              one.
            </Typography>
          </Stack>
        ) : (
          list.promo_codes.length > 0 && (
            <>
              <TableContainer
                tabIndex={0}
                role='region'
                aria-label='Promo codes table'
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  overscrollBehavior: "contain",
                  scrollbarGutter: "stable",
                }}
              >
                <Table
                  size='small'
                  stickyHeader
                  aria-label='Promo codes'
                  sx={{
                    minWidth: 860,
                    "& tr:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        "Code",
                        "Partner",
                        "Discount",
                        "Status",
                        "Expires",
                        "Linked user",
                        "",
                      ].map((label) => (
                        <TableCell
                          key={label || "actions"}
                          align={label ? "left" : "right"}
                          sx={{ ...headCellSx, bgcolor: "background.default" }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.promo_codes.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={bodyCellSx}>
                          <Typography variant='body2' fontWeight={600}>
                            {row.promo_code}
                          </Typography>
                          {row.external_reference_id && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {row.external_reference_id}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {row.partner_name}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {row.discount_value}%
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Chip
                            size='small'
                            label={statusLabel(row)}
                            color={statusColor(row.status, row.is_redeemable)}
                            sx={{ fontWeight: 650 }}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {formatDateTime(row.expires_at)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {row.user?.email || row.user?.name || "—"}
                        </TableCell>
                        <TableCell align='right' sx={bodyCellSx}>
                          <Tooltip title='View user and subscription'>
                            <IconButton
                              aria-label={`View ${row.promo_code}`}
                              onClick={() => openDetail(row.id)}
                            >
                              <VisibilityOutlinedIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Edit code'>
                            <IconButton
                              aria-label={`Edit ${row.promo_code}`}
                              disabled={busy}
                              onClick={() => {
                                setCodeFormError("");
                                setCodeForm(codeFormFromRecord(row));
                              }}
                            >
                              <EditOutlinedIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Delete code'>
                            <IconButton
                              aria-label={`Delete ${row.promo_code}`}
                              disabled={busy}
                              onClick={() => askToDelete(row)}
                            >
                              <DeleteOutlineRoundedIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component='div'
                count={list.total}
                page={list.total ? list.page - 1 : 0}
                rowsPerPage={list.limit}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={(_, page) =>
                  loadCodes({ ...appliedQuery, page: page + 1 })
                }
                onRowsPerPageChange={(event) =>
                  loadCodes({
                    ...appliedQuery,
                    page: 1,
                    limit: Number(event.target.value),
                  })
                }
                sx={{
                  flexShrink: 0,
                  borderTop: 1,
                  borderColor: "divider",
                  "& .MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    px: { xs: 1, sm: 2 },
                  },
                }}
              />
            </>
          )
        )}
      </Paper>

      <PromoCodeFormDialog
        form={codeForm}
        error={codeFormError}
        campaigns={campaigns}
        busy={busy}
        onFormChange={(next) => {
          setCodeFormError("");
          setCodeForm(next);
        }}
        onClose={() => setCodeForm(null)}
        onReview={askToSaveCode}
      />
      <PromoCodeDetailDialog
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={() => setDetail(null)}
      />
    </Stack>
  );
}
